import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowDown, 
  Users, 
  Music, 
  ShoppingCart, 
  XCircle, 
  Bell,
  Save,
  Clock,
  Send,
  CheckCircle,
  TrendingUp,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";
import { format, subDays, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";

interface ReminderTemplate {
  id: string;
  code: string;
  name: string;
  message: string;
  delay_hours: number;
  is_active: boolean;
}

interface FunnelStats {
  totalUsers: number;
  usersWithDemo: number;
  usersWithPurchase: number;
  abandoned: number;
  conversionDemoToPayment: number;
  conversionUserToDemo: number;
}

interface ReminderStats {
  code: string;
  sent: number;
  converted: number;
  conversionRate: number;
}

interface DailyReminderData {
  date: string;
  dateLabel: string;
  sent: number;
  converted: number;
  conversionRate: number;
}

export default function Funnel() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, Partial<ReminderTemplate>>>({});

  // Fetch funnel statistics
  const { data: funnelStats, isLoading: statsLoading } = useQuery({
    queryKey: ['funnel-stats'],
    queryFn: async (): Promise<FunnelStats> => {
      // Get total users
      const { count: totalUsers } = await supabase
        .from('telegram_users')
        .select('*', { count: 'exact', head: true });

      // Get users who created at least one demo
      const { count: usersWithDemo } = await supabase
        .from('telegram_users')
        .select('*', { count: 'exact', head: true })
        .not('last_demo_at', 'is', null);

      // Get users who made at least one purchase
      const { count: usersWithPurchase } = await supabase
        .from('telegram_users')
        .select('*', { count: 'exact', head: true })
        .not('last_purchase_at', 'is', null);

      // Get abandoned (have demo but no purchase)
      const { count: abandoned } = await supabase
        .from('telegram_users')
        .select('*', { count: 'exact', head: true })
        .not('last_demo_at', 'is', null)
        .is('last_purchase_at', null);

      const total = totalUsers || 0;
      const demos = usersWithDemo || 0;
      const purchases = usersWithPurchase || 0;
      const abandonedCount = abandoned || 0;

      return {
        totalUsers: total,
        usersWithDemo: demos,
        usersWithPurchase: purchases,
        abandoned: abandonedCount,
        conversionDemoToPayment: demos > 0 ? Math.round((purchases / demos) * 100) : 0,
        conversionUserToDemo: total > 0 ? Math.round((demos / total) * 100) : 0,
      };
    },
  });

  // Fetch reminder statistics
  const { data: reminderStats, isLoading: reminderStatsLoading } = useQuery({
    queryKey: ['reminder-stats'],
    queryFn: async (): Promise<ReminderStats[]> => {
      const { data: logs, error } = await supabase
        .from('reminder_logs')
        .select('template_code, is_converted');
      
      if (error) throw error;

      // Group by template_code
      const statsMap = new Map<string, { sent: number; converted: number }>();
      
      for (const log of logs || []) {
        const existing = statsMap.get(log.template_code) || { sent: 0, converted: 0 };
        existing.sent++;
        if (log.is_converted) existing.converted++;
        statsMap.set(log.template_code, existing);
      }

      return Array.from(statsMap.entries()).map(([code, stats]) => ({
        code,
        sent: stats.sent,
        converted: stats.converted,
        conversionRate: stats.sent > 0 ? Math.round((stats.converted / stats.sent) * 100) : 0,
      }));
    },
  });

  // Fetch daily reminder chart data (last 14 days)
  const { data: dailyChartData, isLoading: chartLoading } = useQuery({
    queryKey: ['reminder-daily-chart'],
    queryFn: async (): Promise<DailyReminderData[]> => {
      const fourteenDaysAgo = subDays(new Date(), 14);
      
      const { data: logs, error } = await supabase
        .from('reminder_logs')
        .select('sent_at, is_converted')
        .gte('sent_at', fourteenDaysAgo.toISOString())
        .order('sent_at', { ascending: true });
      
      if (error) throw error;

      // Group by day
      const dailyMap = new Map<string, { sent: number; converted: number }>();
      
      // Initialize all 14 days with zeros
      for (let i = 13; i >= 0; i--) {
        const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
        dailyMap.set(date, { sent: 0, converted: 0 });
      }
      
      for (const log of logs || []) {
        const dateKey = format(parseISO(log.sent_at), 'yyyy-MM-dd');
        const existing = dailyMap.get(dateKey) || { sent: 0, converted: 0 };
        existing.sent++;
        if (log.is_converted) existing.converted++;
        dailyMap.set(dateKey, existing);
      }

      return Array.from(dailyMap.entries()).map(([date, stats]) => ({
        date,
        dateLabel: format(parseISO(date), 'd MMM', { locale: ru }),
        sent: stats.sent,
        converted: stats.converted,
        conversionRate: stats.sent > 0 ? Math.round((stats.converted / stats.sent) * 100) : 0,
      }));
    },
  });

  // Fetch reminder templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['reminder-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reminder_templates')
        .select('*')
        .order('delay_hours');
      
      if (error) throw error;
      return data as ReminderTemplate[];
    },
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: async (template: Partial<ReminderTemplate> & { id: string }) => {
      const { error } = await supabase
        .from('reminder_templates')
        .update({
          name: template.name,
          message: template.message,
          delay_hours: template.delay_hours,
          is_active: template.is_active,
        })
        .eq('id', template.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminder-templates'] });
      toast.success('Шаблон сохранён');
      setEditingTemplate(null);
    },
    onError: (error) => {
      toast.error('Ошибка сохранения: ' + error.message);
    },
  });

  const handleEdit = (template: ReminderTemplate) => {
    setEditingTemplate(template.id);
    setFormData(prev => ({
      ...prev,
      [template.id]: { ...template },
    }));
  };

  const handleSave = (id: string) => {
    const data = formData[id];
    if (!data) return;
    updateMutation.mutate({ id, ...data });
  };

  const handleToggleActive = async (template: ReminderTemplate) => {
    const { error } = await supabase
      .from('reminder_templates')
      .update({ is_active: !template.is_active })
      .eq('id', template.id);
    
    if (error) {
      toast.error('Ошибка: ' + error.message);
    } else {
      queryClient.invalidateQueries({ queryKey: ['reminder-templates'] });
      toast.success(template.is_active ? 'Сценарий отключён' : 'Сценарий включён');
    }
  };

  const getTemplateIcon = (code: string) => {
    switch (code) {
      case 'demo_2h': return '⏰';
      case 'demo_24h': return '🔥';
      case 'inactive_7d': return '👋';
      case 'abandoned_1h': return '🎤';
      default: return '📬';
    }
  };

  const formatDelay = (hours: number) => {
    if (hours < 24) return `${hours} ч`;
    return `${Math.floor(hours / 24)} дн`;
  };

  const getStatsForTemplate = (code: string): ReminderStats | undefined => {
    return reminderStats?.find(s => s.code === code);
  };

  return (
    <AdminLayout title="Воронка и напоминания" description="Статистика конверсии и управление дожимающими сериями">
      {/* Funnel Statistics */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <ArrowDown className="w-5 h-5 text-primary" />
          Воронка конверсии
        </h2>
        
        {statsLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {/* Stage 1: All Users */}
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-medium">Всего пользователей</p>
                    <p className="text-sm text-muted-foreground">Зарегистрировались в боте</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{funnelStats?.totalUsers || 0}</p>
                  <p className="text-sm text-muted-foreground">100%</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-muted-foreground" />
            </div>

            {/* Stage 2: Created Demo */}
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Music className="w-6 h-6 text-yellow-500" />
                  <div>
                    <p className="font-medium">Создали демо</p>
                    <p className="text-sm text-muted-foreground">Сгенерировали хотя бы одну песню</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{funnelStats?.usersWithDemo || 0}</p>
                  <p className="text-sm text-primary font-medium">{funnelStats?.conversionUserToDemo || 0}%</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-muted-foreground" />
            </div>

            {/* Stage 3: Purchased */}
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-6 h-6 text-green-500" />
                  <div>
                    <p className="font-medium">Купили полную версию</p>
                    <p className="text-sm text-muted-foreground">Оплатили хотя бы один хит</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{funnelStats?.usersWithPurchase || 0}</p>
                  <p className="text-sm text-primary font-medium">{funnelStats?.conversionDemoToPayment || 0}% от демо</p>
                </div>
              </CardContent>
            </Card>

            {/* Abandoned */}
            <Card className="border-l-4 border-l-red-500 mt-4">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <div>
                    <p className="font-medium">Брошенные</p>
                    <p className="text-sm text-muted-foreground">Создали демо, но не купили</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-red-500">{funnelStats?.abandoned || 0}</p>
                  <p className="text-sm text-muted-foreground">потенциальные клиенты</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Daily Reminder Chart */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          Динамика напоминаний (14 дней)
        </h2>

        <Card>
          <CardContent className="pt-6">
            {chartLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : dailyChartData && dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="dateLabel" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number, name: string) => [
                      value,
                      name === 'sent' ? 'Отправлено' : 
                      name === 'converted' ? 'Конверсий' : name
                    ]}
                  />
                  <Legend 
                    formatter={(value) => 
                      value === 'sent' ? 'Отправлено' : 
                      value === 'converted' ? 'Конверсий' : value
                    }
                  />
                  <Bar 
                    dataKey="sent" 
                    fill="hsl(var(--primary))" 
                    name="sent"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="converted" 
                    fill="hsl(142 76% 36%)" 
                    name="converted"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Пока нет данных о напоминаниях
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion rate line chart */}
        {dailyChartData && dailyChartData.some(d => d.sent > 0) && (
          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Конверсия по дням (%)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="dateLabel" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value}%`, 'Конверсия']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="conversionRate" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reminder Templates */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          Сценарии напоминаний
        </h2>

        {templatesLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {templates?.map(template => {
              const isEditing = editingTemplate === template.id;
              const data = formData[template.id] || template;

              return (
                <Card key={template.id} className={`transition-all ${!template.is_active ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-xl">{getTemplateIcon(template.code)}</span>
                        {isEditing ? (
                          <Input
                            value={data.name || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [template.id]: { ...prev[template.id], name: e.target.value },
                            }))}
                            className="max-w-xs"
                          />
                        ) : (
                          template.name
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          {isEditing ? (
                            <Input
                              type="number"
                              value={data.delay_hours || 0}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                [template.id]: { ...prev[template.id], delay_hours: parseInt(e.target.value) || 0 },
                              }))}
                              className="w-20"
                            />
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {formatDelay(template.delay_hours)}
                            </span>
                          )}
                        </div>
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={() => handleToggleActive(template)}
                        />
                      </div>
                    </div>
                    <CardDescription className="flex items-center gap-4 flex-wrap">
                      <span>Код: <code className="bg-muted px-1 rounded">{template.code}</code></span>
                      {(() => {
                        const stats = getStatsForTemplate(template.code);
                        if (!stats) return null;
                        return (
                          <>
                            <Badge variant="outline" className="gap-1">
                              <Send className="w-3 h-3" />
                              Отправлено: {stats.sent}
                            </Badge>
                            <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                              <CheckCircle className="w-3 h-3" />
                              Конверсий: {stats.converted}
                            </Badge>
                            {stats.sent > 0 && (
                              <Badge variant="secondary" className="gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {stats.conversionRate}%
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isEditing ? (
                      <div className="space-y-3">
                        <Textarea
                          value={data.message || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            [template.id]: { ...prev[template.id], message: e.target.value },
                          }))}
                          rows={5}
                          placeholder="Текст сообщения..."
                        />
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleSave(template.id)}
                            disabled={updateMutation.isPending}
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Сохранить
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingTemplate(null)}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <pre className="text-sm bg-muted p-3 rounded-lg whitespace-pre-wrap font-sans">
                          {template.message}
                        </pre>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEdit(template)}
                        >
                          Редактировать
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
